/**
 * Burton Forms — `<burton-form>` coordinator element (Layer 3).
 *
 * Wraps a real `<form>` (native submit / Enter / autofill preserved) and coordinates its fields:
 * aggregates validity, gates submission, owns the summary region + per-instance ids, and fires
 * `burton:valid-submit` for the app to POST. It never runs a validator — fields validate themselves
 * and report up via bubbling `burton:field-validity`.
 *
 *   <burton-form country="US" locale="en-US" gating="on-submit">
 *     <form novalidate> … <burton-validate> … <button type="submit">Apply</button> </form>
 *   </burton-form>
 *
 * country: ambient ISO country — the controlled single source of truth; may change via a country
 *          selector (data-burton-country-source). `region` is DERIVED from it (deriveRegion).
 * locale: display locale as `language-COUNTRY`, composed in Liquid from
 *         `{{ localization.language.iso_code }}-{{ localization.country.iso_code }}`. Country picks
 *         which options/patterns exist; locale picks their text — independent (e.g. Canada renders
 *         en-CA or fr-CA).
 * gating: "on-submit" (default) validates + focuses first invalid on submit; "live" disables submit
 *         until every field passes.
 */
class BurtonForm extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.fields = this.fields || new Set()
    this.formEl = this.querySelector('form')
    this.gating = this.getAttribute('gating') || 'on-submit'
    this.summaryId = `burton-form-summary-${window.BAO.Forms.nextId()}`
    this.state = new window.BAO.ReactiveState({
      isValid: false,
      isSubmitting: false,
      country: this.getAttribute('country') || null,
      locale: this.getAttribute('locale') || 'en',
    })

    this.renderSummaryRegion()

    this.listeners.add(this, 'burton:field-validity', () =>
      this.onFieldValidity(),
    )

    if (this.formEl) {
      this.listeners.add(this.formEl, 'submit', (event) =>
        this.handleSubmit(event),
      )
    }

    const countrySource = this.querySelector('[data-burton-country-source]')

    if (countrySource) {
      this.listeners.add(countrySource, 'change', (event) =>
        this.setCountry(event.target.value),
      )
    }

    if (this.gating === 'live') {
      this.listeners.add(this, 'input', () => this.refreshValidity())
      this.listeners.add(this, 'change', () => this.refreshValidity())
      this.toggleSubmit(false)
    }
  }

  /**
   * Ambient ISO country — the controlled single source of truth. Reactive.
   *
   * @returns {string|null}
   */
  get country () {
    if (this.state && this.state.has('country')) return this.state.country

    return this.getAttribute('country') || null
  }

  /**
   * Region is DERIVED from country (deriveRegion), never stored — so country stays the one source of
   * truth and region falls out of it.
   *
   * @returns {string|null}
   */
  get region () {
    const derive = window.BAO.Forms && window.BAO.Forms.deriveRegion

    return derive ? derive(this.country) : null
  }

  /**
   * Display language iso code for region-aware field labels. Constant per page load (the storefront
   * locale), so it is not brokered like `region`; defaults to "en" when unset.
   *
   * @returns {string}
   */
  get locale () {
    if (this.state && this.state.has('locale')) return this.state.locale

    return this.getAttribute('locale') || 'en'
  }

  /**
   * The wrapped native <form> element. Consumers use this instead of querySelector('form').
   *
   * @returns {HTMLFormElement|null}
   */
  get formElement () {
    return this.formEl || null
  }

  /**
   * FormData built from the wrapped <form>, ready to POST. Multipart consumers can append to it.
   *
   * @returns {FormData|null}
   */
  get formData () {
    return this.formEl ? new FormData(this.formEl) : null
  }

  /**
   * @param {string} country  ISO country code (from the country source control)
   */
  setCountry (country) {
    if (this.state) this.state.country = country
  }

  /**
   * @param {BurtonValidate} field
   */
  registerField (field) {
    this.fields = this.fields || new Set()
    this.fields.add(field)
  }

  /**
   * The fields that currently participate. Inactive fields (e.g. hidden for the current step/path)
   * are excluded from aggregation, submit validation, and focus — reproducing the old
   * "visible fields only" behavior. Evaluated live so it tracks steps showing/hiding.
   *
   * @returns {BurtonValidate[]}
   */
  activeFields () {
    return [...this.fields].filter((field) =>
      typeof field.isActive === 'function' ? field.isActive() : true,
    )
  }

  /**
   * Aggregates reported validity (fields already rendered their own state). Empty forms are not
   * "valid".
   */
  onFieldValidity () {
    const fields = this.activeFields()
    const isValid =
      fields.length > 0 &&
      fields.every((field) => field.validity.state === 'valid')

    this.state.isValid = isValid

    if (this.gating === 'live') this.toggleSubmit(isValid)
  }

  /**
   * Live-gating recompute: synchronous, no error rendering (that stays on blur). Toggles the submit
   * button as the user types.
   */
  refreshValidity () {
    const fields = this.activeFields()
    const isValid = fields.length > 0 && fields.every((field) => field.check())

    this.state.isValid = isValid

    if (this.gating === 'live') this.toggleSubmit(isValid)
  }

  /**
   * @param {Event} event
   * @returns {Promise<void>}
   */
  async handleSubmit (event) {
    event.preventDefault()

    if (this.state.isSubmitting) return

    const results = await Promise.all(
      this.activeFields().map((field) => field.validate()),
    )

    if (results.length > 0 && results.every((result) => result === true)) {
      this.state.isSubmitting = true
      this.dispatchEvent(
        new CustomEvent('burton:valid-submit', { bubbles: false }),
      )
    } else {
      this.focusFirstInvalid()
    }
  }

  focusFirstInvalid () {
    const invalid = this.activeFields().find(
      (field) => field.validity.state === 'invalid',
    )

    if (!invalid) return

    const control = invalid.inputEl

    if (!control) return

    control.focus()

    if (typeof control.scrollIntoView === 'function') {
      control.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  /**
   * The registered field whose control `name` matches — central field lookup. `setFieldErrors` uses
   * it, and apps use it to drive a specific field's error UI (server errors, code gate, upload).
   *
   * @param {string} name
   * @returns {BurtonValidate|null}
   */
  getField (name) {
    return (
      [...this.fields].find((candidate) => candidate.fieldName === name) || null
    )
  }

  /**
   * Routes server-side (or any origin) errors through the same contract as client validation.
   *
   * @param {Object.<string, string>} errorsByName
   */
  setFieldErrors (errorsByName) {
    Object.entries(errorsByName).forEach(([name, message]) => {
      const field = this.getField(name)

      if (field) field.setInvalid(message)
      else this.setFormError(message)
    })
  }

  /**
   * @param {string} message
   */
  setFormError (message) {
    if (!this.summaryEl) return

    this.summaryEl.textContent = message
  }

  clearFormError () {
    if (!this.summaryEl) return

    this.summaryEl.textContent = ''
  }

  /**
   * The app calls this when its POST settles (success or failure) so the form can submit again.
   * Pairs with the isSubmitting guard set when `burton:valid-submit` fires.
   */
  endSubmit () {
    if (this.state) this.state.isSubmitting = false
  }

  /**
   * Create the form-level error region. Placement, in order:
   * an author-provided `[data-burton-form-summary]` inside the form; otherwise
   * immediately before the submit control; otherwise the end of the form.
   */
  renderSummaryRegion () {
    if (this.summaryEl) return

    const summaryEl = document.createElement('div')

    summaryEl.className = 'bfrm-Form_Summary'
    summaryEl.id = this.summaryId
    summaryEl.setAttribute('role', 'alert')

    const host = this.formEl || this
    const placeholder = host.querySelector('[data-burton-form-summary]')
    const submitControl = this.submitControl()

    if (placeholder) placeholder.replaceWith(summaryEl)
    else if (submitControl) submitControl.before(summaryEl)
    else host.append(summaryEl)

    this.summaryEl = summaryEl
  }

  /**
   * @returns {HTMLElement|null}
   */
  submitControl () {
    return this.formEl ? this.formEl.querySelector('[type="submit"]') : null
  }

  /**
   * @param {boolean} enabled
   */
  toggleSubmit (enabled) {
    const control = this.submitControl()

    if (control) control.disabled = !enabled
  }
}

if (!customElements.get('burton-form')) {
  customElements.define('burton-form', BurtonForm)
}

window.BAO.Forms.FormElement = window.BAO.Forms.FormElement || BurtonForm
